import React, {useEffect, useState, useCallback} from 'react'
import PropTypes from 'prop-types'
import {
    useMultiStyleConfig,
    Text,
    VStack,
    Flex,
    Button,
} from '@salesforce/retail-react-app/app/components/shared/ui'

// COMPONENTS
import usePHPBKApi from '../../../custom-api/hooks/use-phpkb-api'
import {ChevronRight} from '../../../components/icons/arrows'
import SanitizedHtml from '../../../components/html-sanitizer'

// HOOKS
import {useParams, Link} from 'react-router-dom'
import {FormattedMessage} from 'react-intl'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

//Utils
import routingObject from '../../../utils/static-routes'
import {decodeHtmlEntities} from '../../../utils/utils'

export default function ArticlesList({
    setGoBackText,
    setGoBackToPath,
    setPageTitle,
    listOfCategories,
    setIsLoading,
}) {
    const {parent_category_name, category_id} = useParams() || ''
    const {usePHPKBGetArticleFromCategory} = usePHPBKApi()
    const styles = useMultiStyleConfig('Faq', {})
    const {articlesListStyles} = styles || {}
    const [visibleCount, setVisibleCount] = useState(12)
    const {locale} = useMultiSite()

    const {data, isLoading, refetch} = usePHPKBGetArticleFromCategory(
        {
            category_id,
        },
        {
            enabled: false,
        },
    )

    useEffect(() => {
        setIsLoading(isLoading)
    }, [isLoading])

    useEffect(() => {
        if (data) {
            const parentCategoryName = Object.values(listOfCategories?.data || {}).find(
                (category) => category?.category_id === data?.data?.parent_id,
            )?.category_name

            setGoBackToPath(
                parent_category_name
                    ? `${routingObject.FAQ_PATH}/${parent_category_name}/t/${data?.data?.parent_id}`
                    : routingObject.FAQ_PATH,
            )
            setGoBackText(parentCategoryName || 'default')
            setPageTitle(data?.data?.category_name)
        }
    }, [data])

    const memoizedFetchCall = useCallback(() => {
        if (!category_id) return // Prevent calling if category_id is not available
        refetch()
    }, [category_id, refetch])

    useEffect(() => {
        memoizedFetchCall()
    }, [memoizedFetchCall])

    const articles = data?.data?.articles || []

    const handleShowMore = () => {
        setVisibleCount((prevCount) => prevCount + 12)
    }

    const buildUrl = (articleName, articleId) => {
        let cleanName = decodeHtmlEntities(articleName)
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[?]/g, '')
        let url = `${routingObject.FAQ_PATH}/${cleanName}/a/${articleId}`
        return url
    }

    return (
        <VStack w="100%" h="100%">
            <VStack {...articlesListStyles?.outerContainer}>
                <VStack {...articlesListStyles?.middleContainer}>
                    <VStack {...articlesListStyles?.innerContainer}>
                        <VStack
                            position="relative"
                            width="100%"
                            height="auto"
                            {...articlesListStyles?.listWrapper}
                        >
                            {!isLoading &&
                                articles.slice(0, visibleCount).map((article, index) => (
                                    <Flex {...articlesListStyles?.articleWrapper} key={index}>
                                        <Flex {...articlesListStyles?.articleInnerContainer}>
                                            <ChevronRight {...articlesListStyles?.chevronIcon} />
                                            <Link
                                                to={`/${locale?.alias}${buildUrl(
                                                    article?.article_title,
                                                    article?.article_id,
                                                )}`}
                                            >
                                                <SanitizedHtml
                                                    as={Text}
                                                    html={article.article_title}
                                                    {...articlesListStyles?.articleLink}
                                                />
                                            </Link>
                                        </Flex>
                                    </Flex>
                                ))}
                        </VStack>
                    </VStack>
                    <VStack {...articlesListStyles?.toogleCtaContainer}>
                        {visibleCount < articles.length ? (
                            <Button
                                {...articlesListStyles?.toogleCtaButton}
                                onClick={handleShowMore}
                            >
                                <Text {...articlesListStyles?.toogleText}>
                                    <FormattedMessage
                                        id="faq_page.article_list.cta_button.text"
                                        defaultMessage="Show More"
                                    />
                                </Text>
                            </Button>
                        ) : (
                            <></>
                        )}
                    </VStack>
                </VStack>
            </VStack>
        </VStack>
    )
}

ArticlesList.propTypes = {
    setGoBackText: PropTypes.func,
    setPageTitle: PropTypes.func,
    setGoBackToPath: PropTypes.func,
    listOfCategories: PropTypes.object,
    setIsLoading: PropTypes.func,
}
